/**
 * Time off (WALLIYO-0131), as the mockup draws it: a balance, what is waiting
 * on you, who is away today, and your own requests.
 *
 * The order is deliberate. What is waiting on *you* comes before your own,
 * because it is the thing somebody else is held up by. HR's whole-company
 * list comes last, because seeing everybody's leave is the least urgent thing
 * on the screen and the one that must not be mistaken for deciding it.
 */

import type { TimeOffBalance, TimeOffRequest } from "@wallyo/contracts/console/hr";
import { t, type MessageId } from "../../i18n";
import { ErrorNotice } from "../../shell/ErrorNotice";
import { hrHref } from "./hr-route";

export type TimeOffState =
  | { status: "loading"; data: TimeOffData | null }
  | { status: "ready"; data: TimeOffData }
  | { status: "failed"; data: TimeOffData | null; error: unknown };

export type TimeOffData = {
  mine: readonly TimeOffRequest[];
  toDecide: readonly TimeOffRequest[];
  everybody: readonly TimeOffRequest[];
  awayToday: readonly TimeOffRequest[];
  balance: TimeOffBalance;
};

const KIND_LABEL: Record<TimeOffRequest["kind"], MessageId> = {
  annual: "timeOff.kind.annual",
  sick: "timeOff.kind.sick",
  unpaid: "timeOff.kind.unpaid",
  parental: "timeOff.kind.parental",
  other: "timeOff.kind.other",
};

const STATUS_LABEL: Record<TimeOffRequest["status"], MessageId> = {
  pending: "timeOff.status.pending",
  approved: "timeOff.status.approved",
  refused: "timeOff.status.refused",
  withdrawn: "timeOff.status.withdrawn",
};

const STATUS_TONE: Record<TimeOffRequest["status"], string> = {
  pending: "pill warn",
  approved: "pill ok",
  refused: "pill bad",
  withdrawn: "pill",
};

/** A stretch of days, said the way somebody would say it. */
function When({ request }: { request: TimeOffRequest }) {
  if (request.startsOn === request.endsOn) return <>{request.startsOn}</>;
  return (
    <>
      {request.startsOn} → {request.endsOn}
    </>
  );
}

/**
 * How much is left, and what it was worked out from.
 *
 * "No allowance set" is said rather than shown as zero: nobody having said
 * what somebody's allowance is, and somebody having none left, are different
 * facts and one of them is an HR job rather than a refusal.
 */
function Balance({ balance }: { balance: TimeOffBalance }) {
  return (
    <section className="card">
      <h2>{t("timeOff.balance.title")}</h2>
      {balance.allowanceDays === null ? (
        <p className="foot">{t("timeOff.balance.none")}</p>
      ) : (
        <dl className="hr-pairs">
          <dt>{t("timeOff.balance.allowance")}</dt>
          <dd>{t("timeOff.days", { days: String(balance.allowanceDays) })}</dd>
          <dt>{t("timeOff.balance.taken")}</dt>
          <dd>{t("timeOff.days", { days: String(balance.takenDays) })}</dd>
          <dt>{t("timeOff.balance.pending")}</dt>
          <dd>
            {t("timeOff.days", { days: String(balance.pendingDays) })}
            <div className="foot">{t("timeOff.balance.pendingHelp")}</div>
          </dd>
          <dt>{t("timeOff.balance.left")}</dt>
          <dd>
            <strong>{t("timeOff.days", { days: String(balance.leftDays ?? 0) })}</strong>
          </dd>
        </dl>
      )}
    </section>
  );
}

function Rows({
  requests,
  withWho,
  onDecide,
}: {
  requests: readonly TimeOffRequest[];
  withWho: boolean;
  onDecide?: (request: TimeOffRequest, approve: boolean) => void;
}) {
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            {withWho ? <th>{t("timeOff.col.who")}</th> : null}
            <th>{t("timeOff.col.kind")}</th>
            <th>{t("timeOff.col.when")}</th>
            <th>{t("timeOff.col.days")}</th>
            <th>{t("timeOff.col.status")}</th>
            {onDecide ? <th /> : null}
          </tr>
        </thead>
        <tbody>
          {requests.map((request) => (
            <tr key={request.timeOffRequestId}>
              {withWho ? (
                <td>
                  <a href={hrHref({ uuid: request.uuid })}>{request.name}</a>
                </td>
              ) : null}
              <td>{t(KIND_LABEL[request.kind])}</td>
              <td>
                <When request={request} />
                {request.reason ? <div className="foot">{request.reason}</div> : null}
              </td>
              <td className="num">{request.days}</td>
              <td>
                <span className={STATUS_TONE[request.status]}>{t(STATUS_LABEL[request.status])}</span>
                {request.decidedByName ? (
                  <div className="foot">{t("timeOff.decidedBy", { name: request.decidedByName })}</div>
                ) : null}
                {request.decisionNote ? <div className="foot">{request.decisionNote}</div> : null}
              </td>
              {onDecide ? (
                <td className="num">
                  <div className="btns">
                    <button className="btn small" type="button" onClick={() => onDecide(request, true)}>
                      {t("timeOff.approve")}
                    </button>
                    <button className="btn quiet small" type="button" onClick={() => onDecide(request, false)}>
                      {t("timeOff.refuse")}
                    </button>
                  </div>
                </td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function TimeOffView(props: {
  state: TimeOffState;
  onAsk: () => void;
  onDecide: (request: TimeOffRequest, approve: boolean) => void;
  onRetry: () => void;
}) {
  const { state } = props;
  const data = state.data;

  return (
    <>
      <div className="hr-search">
        <div className="grow" />
        <button className="btn" type="button" onClick={props.onAsk} disabled={state.status !== "ready"}>
          {t("timeOff.ask")}
        </button>
      </div>

      {state.status === "failed" ? (
        <div className="audit-error">
          <p className="foot">{t("hr.failed")}</p>
          <ErrorNotice error={state.error} />
          <div className="btns">
            <button className="btn" type="button" onClick={props.onRetry}>
              {t("hr.retry")}
            </button>
          </div>
        </div>
      ) : null}

      {!data && state.status === "loading" ? (
        <div className="empty" role="status">
          <span className="spinner" aria-hidden="true" />
          <span>{t("hr.loading")}</span>
        </div>
      ) : null}

      {data ? (
        <>
          {/* What somebody else is held up by, before anything of your own. */}
          {data.toDecide.length > 0 ? (
            <section className="card">
              <h2>{t("timeOff.toDecide.title")}</h2>
              <p className="foot">{t("timeOff.toDecide.help")}</p>
              <Rows requests={data.toDecide} withWho onDecide={props.onDecide} />
            </section>
          ) : null}

          <Balance balance={data.balance} />

          <section className="card">
            <h2>{t("timeOff.mine.title")}</h2>
            {data.mine.length === 0 ? (
              <p className="foot">{t("timeOff.mine.none")}</p>
            ) : (
              <Rows requests={data.mine} withWho={false} />
            )}
          </section>

          <section className="card">
            <h2>{t("timeOff.away.title")}</h2>
            {data.awayToday.length === 0 ? (
              <p className="foot">{t("timeOff.away.none")}</p>
            ) : (
              <ul className="hr-plain">
                {data.awayToday.map((request) => (
                  <li key={request.timeOffRequestId}>
                    <a href={hrHref({ uuid: request.uuid })}>{request.name}</a>{" "}
                    <span className="foot">
                      · {t(KIND_LABEL[request.kind])} · <When request={request} />
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* HR sees everybody's. Last, and never with a decide button: seeing
              everybody's leave is not approving everybody's leave. */}
          {data.everybody.length > 0 ? (
            <section className="card">
              <h2>{t("timeOff.everybody.title")}</h2>
              <p className="foot">{t("timeOff.everybody.help")}</p>
              <Rows requests={data.everybody} withWho />
            </section>
          ) : null}
        </>
      ) : null}
    </>
  );
}
